// screens/GuidedLessonScreen.tsx
//
// Canonical projected lesson reader. The route is
// /studia/lezione/guidata/:argomentoId/:lessonId, where lessonId is the
// micro-topic lesson id. Each mount starts, resumes, or restarts a canonical
// lesson_attempt and persists embedded quiz answers through that attempt.

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { ProfMessage } from '../components/ProfMessage';
import { ProfProse } from '../components/ProfProse';
import { InlineImage } from '../components/InlineImage';
import { SelectProfFigureButton } from '../components/SelectProfFigureButton';
import { VeroFalsoReplies, type VFAnswer } from '../components/QuestionCard';
import { useTokens } from '../state/theme';
import { useNavOverride } from '../state/nav-override';
import type { QuizAnswer as BaseQuizAnswer } from '../state/guided-lesson-progress';
import { useActiveLessonContext } from '../state/active-lesson-context';
import { useReducedMotion } from '../state/use-reduced-motion';
import { READING_DOCK_H } from '../tokens';
import {
  deriveGuidedLessonProgressState,
  guidedFinishCopy,
  guidedLessonTitle,
  lessonAssetSrc,
  normalizeLessonAssetRef,
  questionRefsForChapter,
  visibleAssetRefsForChapter,
  type GuidedLessonProgressSummary,
} from '../state/canonical-learning';
import {
  lezioneApi,
  type GuidedLessonResponse,
  type GuidedLessonChapter,
  type GuidedLessonSegment,
  type GuidedLessonAttempt,
  type GuidedLessonAttemptAnswer,
} from '../api/lezione';
import { ApiError } from '../api/client';

type QuizAnswer = BaseQuizAnswer & { feedback?: { whyMd: string; repairMd: string } | null };

function acknowledgedQuizAnswer(value: unknown, attemptId: string, sourceOrder: number, quizIndex: number): QuizAnswer | null {
  if (!value || typeof value !== 'object') return null;
  const saved = value as Record<string, unknown>;
  const answer = saved.answer as Record<string, unknown> | undefined;
  const feedback = saved.feedback as { whyMd?: unknown; repairMd?: unknown } | null;
  if (!answer || !['committed', 'legacy_unverified'].includes(String(saved.mastery_status))
    || typeof saved.replayed !== 'boolean' || answer.attempt_id !== attemptId
    || answer.source_chapter_order !== sourceOrder || answer.quiz_index !== quizIndex
    || !Number.isInteger(answer.selected_index) || Number(answer.selected_index) < 0
    || !Number.isInteger(answer.correct_index) || Number(answer.correct_index) < 0
    || typeof answer.is_correct !== 'boolean'
    || answer.is_correct !== (answer.selected_index === answer.correct_index)
    || (saved.mastery_status === 'committed' && (!feedback
      || typeof feedback.whyMd !== 'string' || typeof feedback.repairMd !== 'string'))
    || (saved.mastery_status === 'legacy_unverified' && (feedback !== null || saved.replayed !== true))) return null;
  return { picked: Number(answer.selected_index), correct: answer.is_correct,
    responseTimeMs: typeof answer.response_time_ms === 'number' ? answer.response_time_ms : 0,
    feedback: feedback as QuizAnswer['feedback'] };
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'unavailable' } // 404 — flag off / no baked lesson
  | { kind: 'error'; message: string; recovery?: 'restart' }
  | { kind: 'ready'; lesson: GuidedLessonResponse; attempt: GuidedLessonAttempt };

const GUIDED_PENDING_ANSWERS_KEY = 'asc_guided_pending_answers_v1';
const GUIDED_PENDING_ANSWERS_VERSION = 1 as const;

interface PendingGuidedAnswer {
  attemptId: string;
  argomentoId: string;
  lessonId: string;
  sourceChapterOrder: number;
  displayChapterOrder: number;
  quizIndex: number;
  selectedIndex: number;
  responseTimeMs: number;
  savedAt: number;
}

function isPendingGuidedAnswer(value: unknown): value is PendingGuidedAnswer {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.attemptId === 'string' && row.attemptId.length > 0
    && typeof row.argomentoId === 'string' && row.argomentoId.length > 0
    && typeof row.lessonId === 'string' && row.lessonId.length > 0
    && Number.isInteger(row.sourceChapterOrder) && Number(row.sourceChapterOrder) > 0
    && Number.isInteger(row.displayChapterOrder) && Number(row.displayChapterOrder) > 0
    && Number.isInteger(row.quizIndex) && Number(row.quizIndex) >= 0
    && Number.isInteger(row.selectedIndex) && Number(row.selectedIndex) >= 0
    && Number.isInteger(row.responseTimeMs) && Number(row.responseTimeMs) >= 0
    && typeof row.savedAt === 'number' && Number.isFinite(row.savedAt) && row.savedAt > 0;
}

function loadPendingGuidedAnswers(): PendingGuidedAnswer[] {
  try {
    const raw = localStorage.getItem(GUIDED_PENDING_ANSWERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { version?: unknown; rows?: unknown };
    if (
      parsed?.version !== GUIDED_PENDING_ANSWERS_VERSION
      || !Array.isArray(parsed.rows)
      || parsed.rows.length > 20
      || !parsed.rows.every(isPendingGuidedAnswer)
    ) {
      localStorage.removeItem(GUIDED_PENDING_ANSWERS_KEY);
      return [];
    }
    const seen = new Set<string>();
    for (const row of parsed.rows) {
      const key = `${row.attemptId}:${row.sourceChapterOrder}:${row.quizIndex}`;
      if (seen.has(key)) {
        localStorage.removeItem(GUIDED_PENDING_ANSWERS_KEY);
        return [];
      }
      seen.add(key);
    }
    return parsed.rows;
  } catch {
    try { localStorage.removeItem(GUIDED_PENDING_ANSWERS_KEY); } catch { /* best effort */ }
    return [];
  }
}

function writePendingGuidedAnswers(rows: PendingGuidedAnswer[]): boolean {
  try {
    if (rows.length === 0) {
      localStorage.removeItem(GUIDED_PENDING_ANSWERS_KEY);
    } else {
      localStorage.setItem(GUIDED_PENDING_ANSWERS_KEY, JSON.stringify({
        version: GUIDED_PENDING_ANSWERS_VERSION,
        rows,
      }));
    }
    return true;
  } catch {
    return false;
  }
}

function savePendingGuidedAnswer(row: PendingGuidedAnswer): boolean {
  const identity = `${row.attemptId}:${row.sourceChapterOrder}:${row.quizIndex}`;
  const retained = loadPendingGuidedAnswers().filter((candidate) => (
    candidate.attemptId === row.attemptId
    && `${candidate.attemptId}:${candidate.sourceChapterOrder}:${candidate.quizIndex}` !== identity
  ));
  return writePendingGuidedAnswers([...retained, row]);
}

function removePendingGuidedAnswer(attemptId: string, sourceChapterOrder: number, quizIndex: number): void {
  const next = loadPendingGuidedAnswers().filter((row) => !(
    row.attemptId === attemptId
    && row.sourceChapterOrder === sourceChapterOrder
    && row.quizIndex === quizIndex
  ));
  writePendingGuidedAnswers(next);
}

function pendingAnswersForAttempt(
  attempt: GuidedLessonAttempt,
  lesson: GuidedLessonResponse,
  canonicalAnswers: Record<string, QuizAnswer>,
): PendingGuidedAnswer[] {
  const valid: PendingGuidedAnswer[] = [];
  for (const row of loadPendingGuidedAnswers()) {
    if (
      row.attemptId !== attempt.id
      || row.argomentoId !== attempt.argomentoId
      || row.lessonId !== attempt.lessonId
    ) continue;
    const key = `${row.sourceChapterOrder}:${row.quizIndex}`;
    if (canonicalAnswers[key]) continue;
    const chapter = lesson.chapters.find((candidate) => sourceOrderOf(candidate) === row.sourceChapterOrder);
    const quiz = chapter?.segments.filter((segment) => segment.kind === 'quiz')[row.quizIndex];
    if (
      !chapter
      || displayOrderOf(chapter) !== row.displayChapterOrder
      || !quiz
      || row.selectedIndex >= quiz.quiz.options.length
    ) continue;
    valid.push(row);
  }
  writePendingGuidedAnswers(valid);
  return valid;
}

export default function GuidedLessonScreen(): JSX.Element {
  const { argomentoId = '', lessonId = '' } = useParams<{ argomentoId: string; lessonId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useTokens();
  const { setNavActions } = useNavOverride();
  const { goBack } = useShellNav();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [currentChapter, setCurrentChapter] = useState(1);
  const [answers, setAnswers] = useState<Record<string, QuizAnswer>>({});
  const [pendingAnswers, setPendingAnswers] = useState<Record<string, boolean>>({});
  const [failedAnswers, setFailedAnswers] = useState<Record<string, QuizAnswer>>({});
  const answersRef = useRef<Record<string, QuizAnswer>>({});
  const currentChapterRef = useRef(1);
  const [gateMessage, setGateMessage] = useState<string | null>(null);
  const restartAttempt = searchParams.get('restart') === '1';
  // Item 2 — publish the active lesson context so the in-lesson Prof chat (the
  // swipe-up sheet, mounted above <Routes>) can bias the FULL Prof toward this
  // lesson and never refuse a driving/exam question.
  const setLessonContext = useActiveLessonContext((s) => s.setContext);
  const clearLessonContext = useActiveLessonContext((s) => s.clearContext);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    currentChapterRef.current = currentChapter;
  }, [currentChapter]);

  useEffect(() => {
    let cancelled = false;
    setFinished(false);
    setGateMessage(null);
    answersRef.current = {};
    currentChapterRef.current = 1;
    setAnswers({});
    setPendingAnswers({});
    setFailedAnswers({});
    (async () => {
      if (!argomentoId || !lessonId) {
        setPhase({ kind: 'error', message: 'Lezione non trovata.' });
        return;
      }
      setPhase({ kind: 'loading' });
      try {
        const started = await lezioneApi.guidedAttemptStart({
          argomentoId,
          lessonId,
          restart: restartAttempt,
        });
        if (cancelled) return;
        const restoredAnswers = answersFromAttemptRows(started.answers);
        const pendingToReplay = pendingAnswersForAttempt(started.attempt, started.lesson, restoredAnswers);
        answersRef.current = restoredAnswers;
        setAnswers(restoredAnswers);
        setCurrentChapter(resumeChapterFor(started.lesson, restoredAnswers, started.attempt));
        setPhase({ kind: 'ready', lesson: started.lesson, attempt: started.attempt });
        if (restartAttempt) {
          navigate(`/studia/lezione/guidata/${argomentoId}/${lessonId}`, { replace: true });
        }
        for (const pending of pendingToReplay) {
          if (cancelled) return;
          const pendingChapter = started.lesson.chapters.find(
            (candidate) => sourceOrderOf(candidate) === pending.sourceChapterOrder,
          );
          const pendingQuiz = pendingChapter?.segments.filter((segment) => segment.kind === 'quiz')[pending.quizIndex];
          if (!pendingChapter || !pendingQuiz) continue;
          await recordGuidedQuizAnswer(started.attempt, pendingChapter, pending.quizIndex, {
            picked: pending.selectedIndex,
            correct: pending.selectedIndex === pendingQuiz.quiz.correct_index,
            responseTimeMs: pending.responseTimeMs,
          });
        }
      } catch (err) {
        if (cancelled) return;
        // A 404 means the server flag is OFF / no baked lesson — degrade to a
        // friendly "not available yet" rather than a hard error.
        if (err instanceof ApiError && err.status === 404) {
          setPhase({ kind: 'unavailable' });
          return;
        }
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          (err.body as { error?: string } | null)?.error === 'lesson_content_stale'
        ) {
          setPhase({
            kind: 'error',
            message: 'Questa lezione è stata aggiornata. Riaprila per usare la versione corretta.',
            recovery: 'restart',
          });
          return;
        }
        setPhase({
          kind: 'error',
          message: 'Non sono riuscito ad aprire la lezione guidata. Riprova tra poco.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // currentChapter intentionally excluded — we clamp once on load, not on
    // every chapter change (that would refetch the lesson on navigation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argomentoId, lessonId, restartAttempt]);

  // ---------------------------------------------------------------------------
  // Chapter navigation
  // ---------------------------------------------------------------------------
  function goToChapter(lesson: GuidedLessonResponse, next: number): void {
    const fromChapter = currentChapterRef.current;
    const savedAnswers = answersRef.current;
    const clamped = Math.max(1, Math.min(next, lesson.chapters.length));
    if (!canMoveToChapter(lesson, fromChapter, clamped, savedAnswers)) {
      setGateMessage('Rispondi alle domande di questo capitolo prima di continuare.');
      return;
    }
    setGateMessage(null);
    setCurrentChapter(clamped);
    // Scroll the reader back to the top on chapter change.
    const main = document.getElementById('guided-lesson-main');
    if (main) main.scrollTop = 0;
  }

  // Finish is scored only after every required projected-lesson quiz has a
  // locked canonical answer.
  async function finishGuidedLesson(lesson: GuidedLessonResponse, attempt: GuidedLessonAttempt): Promise<void> {
    const savedAnswers = answersRef.current;
    const missing = firstIncompleteChapter(lesson, savedAnswers);
    if (missing) {
      setGateMessage('Rispondi alle domande di questo capitolo prima di continuare.');
      setCurrentChapter(missing.order);
      return;
    }
    try {
      await lezioneApi.guidedAttemptComplete(attempt.id);
      setFinished(true);
    } catch {
      setGateMessage('Non sono riuscito a salvare il completamento. Riprova tra poco.');
    }
  }

  async function recordGuidedQuizAnswer(
    attempt: GuidedLessonAttempt,
    chapter: GuidedLessonChapter,
    quizIndex: number,
    answer: QuizAnswer,
  ): Promise<void> {
    const key = guidedQuizKey(chapter, quizIndex);
    const retained = loadPendingGuidedAnswers().find(row => row.attemptId === attempt.id
      && row.sourceChapterOrder === sourceOrderOf(chapter) && row.quizIndex === quizIndex);
    const request = {
      sourceChapterOrder: sourceOrderOf(chapter),
      displayChapterOrder: displayOrderOf(chapter),
      quizIndex,
      selectedIndex: retained?.selectedIndex ?? answer.picked,
      responseTimeMs: retained?.responseTimeMs ?? answer.responseTimeMs ?? 0,
    };
    if (!savePendingGuidedAnswer({
      attemptId: attempt.id,
      argomentoId: attempt.argomentoId,
      lessonId: attempt.lessonId,
      ...request,
      savedAt: Date.now(),
    })) {
      setGateMessage('Non sono riuscito a preparare il salvataggio. Riprova.');
      return;
    }
    setPendingAnswers((prev) => ({ ...prev, [key]: true }));
    const accept = (canonical: QuizAnswer) => {
      removePendingGuidedAnswer(attempt.id, request.sourceChapterOrder, quizIndex);
      setAnswers(prev => {
        const next = { ...prev, [key]: canonical }; answersRef.current = next; return next;
      });
      setFailedAnswers(prev => { const next = { ...prev }; delete next[key]; return next; });
      setGateMessage(null);
    };
    try {
      const saved = await lezioneApi.guidedAttemptAnswer(attempt.id, request);
      const canonical = acknowledgedQuizAnswer(saved, attempt.id, request.sourceChapterOrder, quizIndex);
      if (saved.ok !== true || !canonical || canonical.picked !== request.selectedIndex) throw new Error('lesson_answer_acknowledgement_invalid');
      accept(canonical);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const canonical = acknowledgedQuizAnswer(err.body, attempt.id, request.sourceChapterOrder, quizIndex);
        if (canonical) { accept(canonical); return; }
        try {
          const resumed = await lezioneApi.guidedAttemptStart({
            argomentoId: attempt.argomentoId,
            lessonId: attempt.lessonId,
          });
          const restored = answersFromAttemptRows(resumed.answers);
          answersRef.current = restored;
          setAnswers(restored);
          if (restored[key]) {
            accept(restored[key]);
            return;
          }
        } catch {
          // Fall through to the retry copy below.
        }
      }
      setFailedAnswers(prev => ({ ...prev, [key]: { ...answer, picked: request.selectedIndex, responseTimeMs: request.responseTimeMs } }));
      setGateMessage('Non sono riuscito a salvare la risposta. Riprova.');
    } finally {
      setPendingAnswers((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function exitFinishedLesson(): Promise<void> {
    navigate(`/studia/argomento/${argomentoId}`);
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const lesson = phase.kind === 'ready' ? phase.lesson : null;
  const attempt = phase.kind === 'ready' ? phase.attempt : null;
  const chapter = lesson?.chapters.find((ch) => ch.order === currentChapter) ?? lesson?.chapters[0] ?? null;
  const finishSummary = lesson ? deriveGuidedLessonProgressState({
    currentChapter,
    chapterCount: lesson.chapters.length,
    completed: true,
    answers,
  }, lesson.quiz_count) : null;
  const finishCopy = lesson && finishSummary
    ? guidedFinishCopy(finishSummary, guidedLessonTitle(lesson), lesson.chapters.length)
    : null;

  // Gesture chapter paging: register imperative onNext / onBack so the universal
  // swipe / edge-tap moves between chapters. On the final chapter, forward opens
  // the finish page.
  useEffect(() => {
    if (phase.kind !== 'ready' || !lesson || !attempt || !chapter || finished) {
      setNavActions({});
      return;
    }
    const isLast = chapter.order >= lesson.chapters.length;
    const onNext = isLast ? () => void finishGuidedLesson(lesson, attempt) : () => goToChapter(lesson, chapter.order + 1);
    const onBack = chapter.order > 1 ? () => goToChapter(lesson, chapter.order - 1) : undefined;
    setNavActions({ onNext, onBack });
    return () => setNavActions({});
    // goToChapter is stable across renders for a given lesson; chapter.order is
    // the real dependency that re-registers the actions per chapter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, lesson, attempt, chapter?.order, finished, setNavActions, answers]);

  // Item 2 — publish the active lesson context (argomento + current chapter) for
  // the in-lesson Prof chat, refreshed per chapter, cleared on unmount so the
  // global /prof thread never carries a stale lesson bias.
  useEffect(() => {
    if (phase.kind !== 'ready' || !lesson || !chapter) return;
    setLessonContext({
      argomentoId,
      argomentoName: lesson.argomento_name,
      lessonTitle: guidedLessonTitle(lesson),
      chapterTitle: chapter.title,
      chapterOrder: chapter.order,
      chapterCount: lesson.chapters.length,
      visibleAssetRefs: visibleAssetRefsForChapter(chapter),
      questionRefs: questionRefsForChapter(chapter),
    });
    return () => clearLessonContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, lesson, chapter?.order, argomentoId]);

  return (
    <Screen>
      {/* V29 S3: clean reading chrome — back arrow + chapter position only.
          The old "Progressi"/"Lezioni" pill pair duplicated the back arrow
          (back → drill-down → back → cockpit) and crowded the title. */}
      <TopChrome
        title={lesson ? guidedLessonTitle(lesson) : 'Lezione'}
        subtitle={finished ? 'Completata' : chapter ? `Capitolo ${chapter.order} di ${lesson?.chapters.length}` : 'Studia col Prof'}
        onBack={goBack}
      />

      <main
        id="guided-lesson-main"
        // Bottom padding clears the reading dock + the floating ChatDock pill.
        // V29 S3 (audit A3): reader prose suppresses text selection — a touch
        // drag on lesson text used to light up selection highlights instead of
        // scrolling; lesson text isn't for copying (the chat is selectable).
        style={{
          flex: 1,
          overflow: 'auto',
          padding: `16px 16px calc(${READING_DOCK_H}px + 84px + var(--sai-bottom, 0px))`,
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        {phase.kind === 'loading' && (
          <div
            data-testid="guided-loading"
            style={{ padding: '32px 16px', textAlign: 'center', fontFamily: t.fonts.body, fontSize: 14, color: t.textMuted, lineHeight: 1.5 }}
          >
            Sto aprendo la lezione guidata…
          </div>
        )}

        {phase.kind === 'unavailable' && (
          <div
            data-testid="guided-unavailable"
            role="status"
            style={{ padding: '28px 16px', textAlign: 'center', fontFamily: t.fonts.body, fontSize: 14, color: t.text, lineHeight: 1.55 }}
          >
            La lezione guidata per questo argomento non è ancora disponibile.
            <div style={{ marginTop: 14 }}>
              <button
                onClick={() => navigate(`/studia/argomento/${argomentoId}`)}
                style={secondaryBtn(t)}
              >
                Torna alle lezioni
              </button>
            </div>
          </div>
        )}

        {phase.kind === 'error' && (
          <div
            data-testid="guided-error"
            role="alert"
            style={{ padding: '24px 16px', textAlign: 'center', fontFamily: t.fonts.body, fontSize: 14, color: t.text, lineHeight: 1.5 }}
          >
            {phase.message}
            {phase.recovery === 'restart' && (
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => navigate(`/studia/lezione/guidata/${argomentoId}/${lessonId}?restart=1`, { replace: true })}
                  style={secondaryBtn(t)}
                >
                  Riapri la lezione aggiornata
                </button>
              </div>
            )}
          </div>
        )}

        {phase.kind === 'ready' && lesson && chapter && finished && (
          <GuidedCelebration
            copy={finishCopy}
            stats={finishSummary}
            chapterCount={lesson.chapters.length}
            onReturn={exitFinishedLesson}
          />
        )}

        {phase.kind === 'ready' && lesson && chapter && !finished && (
          <ChapterView
            key={chapter.order}
            argomentoId={argomentoId}
            chapter={chapter}
            answers={answers}
            pendingAnswers={pendingAnswers}
            failedAnswers={failedAnswers}
            onAnswerQuiz={(quizIndex, ans) => {
              setGateMessage(null);
              if (attempt) void recordGuidedQuizAnswer(attempt, chapter, quizIndex, ans);
            }}
          />
        )}
      </main>

      {/* V29 S3 — the READING DOCK: fixed bottom bar with Indietro · progress
          rail + "Capitolo N di M" · Avanti. Replaces the in-content footer AND
          the top progress rail (approved design). Same handlers/gating as the
          registered navActions: goToChapter enforces the answered-quiz gate,
          the last chapter finishes the lesson. */}
      {phase.kind === 'ready' && lesson && chapter && !finished && (
        <>
          {gateMessage && (
            /* The quiz gate rides the dock as a floating pill — never an
               alert box shoved above the prose. Bottom offset clears the
               ChatDock pill band (READING_DOCK_H + 14px + ~48px pill): the
               S9 live E2E caught the two pills sharing the same strip, with
               the gate text truncated behind "Chiedi al Prof". */
            <div
              data-testid="guided-gate-message"
              role="alert"
              style={{
                position: 'absolute',
                left: 16,
                right: 16,
                bottom: `calc(${READING_DOCK_H}px + var(--sai-bottom, 0px) + 72px)`,
                zIndex: 46,
                padding: '10px 14px',
                borderRadius: 999,
                // wrongSoft is translucent (10-16% alpha) — layer it over the
                // opaque surface so the prose underneath can't bleed through.
                background: `linear-gradient(${t.wrongSoft}, ${t.wrongSoft}), ${t.surface}`,
                border: `1px solid ${t.wrong}`,
                color: t.wrongText,
                fontFamily: t.fonts.body,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.35,
                textAlign: 'center',
                boxShadow: '0 8px 22px rgba(26, 21, 53, 0.14)',
              }}
            >
              {gateMessage}
            </div>
          )}
          <nav
            data-testid="guided-chapter-nav"
            aria-label="Navigazione capitoli"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 45,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              height: `calc(${READING_DOCK_H}px + var(--sai-bottom, 0px))`,
              padding: `10px 14px calc(10px + var(--sai-bottom, 0px))`,
              background: t.mode === 'dark' ? 'rgba(22,16,31,0.9)' : 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              borderTop: `1px solid ${t.line}`,
            }}
          >
            <button
              type="button"
              data-testid="guided-prev"
              aria-label="Capitolo precedente"
              onClick={() => goToChapter(lesson, chapter.order - 1)}
              disabled={chapter.order <= 1}
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                border: `1px solid ${t.lineStrong}`,
                background: t.surface,
                color: chapter.order <= 1 ? t.textFaint : t.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: chapter.order <= 1 ? 'not-allowed' : 'pointer',
                opacity: chapter.order <= 1 ? 0.5 : 1,
                flexShrink: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
            </button>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
              <div
                data-testid="guided-progress-rail"
                aria-label="Capitoli della lezione"
                style={{ display: 'flex', gap: 4, width: '100%', maxWidth: 180 }}
              >
                {lesson.chapters.map((ch) => (
                  <button
                    type="button"
                    key={ch.order}
                    aria-label={`Vai al capitolo ${ch.order}: ${ch.title}`}
                    onClick={() => goToChapter(lesson, ch.order)}
                    style={{
                      flex: 1,
                      minWidth: 6,
                      height: 4,
                      borderRadius: 999,
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      background:
                        ch.order < chapter.order ? t.right
                        : ch.order === chapter.order ? t.accent
                        : t.line,
                    }}
                  />
                ))}
              </div>
              <span style={{ fontFamily: t.fonts.body, fontSize: 11.5, fontWeight: 700, color: t.textMuted }}>
                Capitolo {chapter.order} di {lesson.chapters.length}
              </span>
            </div>
            <button
              type="button"
              data-testid="guided-next"
              aria-label={chapter.order >= lesson.chapters.length ? 'Fine lezione' : 'Capitolo successivo'}
              onClick={() => {
                if (chapter.order >= lesson.chapters.length) {
                  if (attempt) void finishGuidedLesson(lesson, attempt);
                } else {
                  goToChapter(lesson, chapter.order + 1);
                }
              }}
              style={{
                minHeight: 44,
                minWidth: 44,
                padding: chapter.order >= lesson.chapters.length ? '0 16px' : 0,
                borderRadius: chapter.order >= lesson.chapters.length ? 12 : '50%',
                border: 'none',
                background: t.brandGradient,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: t.fonts.body,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 3px 12px rgba(105, 67, 255, 0.28)',
                flexShrink: 0,
              }}
            >
              {chapter.order >= lesson.chapters.length
                ? 'Fine lezione'
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>}
            </button>
          </nav>
        </>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// ChapterView — renders one chapter's interleaved blocks + quizzes.
// ---------------------------------------------------------------------------

function sourceOrderOf(chapter: GuidedLessonChapter): number {
  return chapter.source_chapter_order ?? chapter.order;
}

function displayOrderOf(chapter: GuidedLessonChapter): number {
  return chapter.display_chapter_order ?? chapter.order;
}

function guidedQuizKey(chapter: GuidedLessonChapter, quizIndex: number): string {
  return `${sourceOrderOf(chapter)}:${quizIndex}`;
}

function quizCount(chapter: GuidedLessonChapter): number {
  return chapter.segments.filter((segment) => segment.kind === 'quiz').length;
}

function chapterComplete(chapter: GuidedLessonChapter, answers: Record<string, QuizAnswer>): boolean {
  const count = quizCount(chapter);
  if (count === 0) return true;
  for (let i = 0; i < count; i += 1) {
    if (!answers[guidedQuizKey(chapter, i)]) return false;
  }
  return true;
}

function firstIncompleteChapter(
  lesson: GuidedLessonResponse,
  answers: Record<string, QuizAnswer>,
): GuidedLessonChapter | null {
  return lesson.chapters.find((chapter) => !chapterComplete(chapter, answers)) ?? null;
}

function canMoveToChapter(
  lesson: GuidedLessonResponse,
  current: number,
  target: number,
  answers: Record<string, QuizAnswer>,
): boolean {
  if (target <= current) return true;
  const blocking = lesson.chapters
    .filter((chapter) => chapter.order >= current && chapter.order < target)
    .find((chapter) => !chapterComplete(chapter, answers));
  if (blocking) {
    return false;
  }
  return true;
}

function answersFromAttemptRows(rows: GuidedLessonAttemptAnswer[]): Record<string, QuizAnswer> {
  const out: Record<string, QuizAnswer> = {};
  for (const row of rows) {
    out[`${row.source_chapter_order}:${row.quiz_index}`] = {
      picked: row.selected_index,
      correct: row.is_correct,
      responseTimeMs: row.response_time_ms ?? 0,
      feedback: row.feedback ?? null,
    };
  }
  return out;
}

function resumeChapterFor(
  lesson: GuidedLessonResponse,
  answers: Record<string, QuizAnswer>,
  attempt: GuidedLessonAttempt,
): number {
  if (attempt.status === 'completed') return 1;
  const firstMissing = firstIncompleteChapter(lesson, answers);
  if (firstMissing) return firstMissing.order;
  const current = lesson.chapters.find((chapter) => sourceOrderOf(chapter) === attempt.currentSourceChapterOrder);
  return current?.order ?? 1;
}

function ChapterView({
  argomentoId,
  chapter,
  answers,
  pendingAnswers,
  failedAnswers,
  onAnswerQuiz,
}: {
  argomentoId: string;
  chapter: GuidedLessonChapter;
  answers: Record<string, QuizAnswer>;
  pendingAnswers: Record<string, boolean>;
  failedAnswers: Record<string, QuizAnswer>;
  onAnswerQuiz: (quizIndex: number, answer: QuizAnswer) => void;
}): JSX.Element {
  const t = useTokens();
  // Quiz indexes are assigned in segment order so the persisted key is stable.
  // Derive them without mutating a render-local counter inside JSX.
  const quizIndexes = chapter.segments.map((_, segmentIndex) => (
    chapter.segments.slice(0, segmentIndex + 1).filter((segment) => segment.kind === 'quiz').length - 1
  ));
  void argomentoId; // kept for potential per-argomento analytics later

  return (
    <article data-testid={`guided-chapter-${chapter.order}`}>
      <h1
        style={{
          fontFamily: t.fonts.serif,
          fontSize: 22,
          fontWeight: 700,
          color: t.text,
          letterSpacing: -0.3,
          lineHeight: 1.25,
          margin: '0 0 14px',
        }}
      >
        {chapter.title}
      </h1>

      {/* Chapter/subsection-owned lesson images. signImageRefs remains a fallback
          for older responses only. */}
      <ChapterSignImages
        refs={chapter.lessonImageRefs ?? chapter.signImageRefs ?? []}
        images={chapter.lessonImages ?? chapter.signImages}
        chapterTitle={chapter.title}
      />

      {chapter.segments.map((seg, i) => {
        if (seg.kind === 'quiz') {
          const idx = quizIndexes[i]!;
          const key = guidedQuizKey(chapter, idx);
          return (
            <QuizCard
              key={`q-${i}`}
              segment={seg}
              answer={answers[key]}
              pending={Boolean(pendingAnswers[key])}
              retryAnswer={failedAnswers[key]}
              onAnswer={(ans) => onAnswerQuiz(idx, ans)}
            />
          );
        }
        return <BlockSegment key={`b-${i}`} segment={seg} chapterTitle={chapter.title} />;
      })}

      {/* In-lesson Prof — opens the lesson-scoped swipe-up sheet (NOT the global
          /prof thread), per in-lesson-chat-confined. */}
    </article>
  );
}

// ---------------------------------------------------------------------------
// ChapterSignImages — Item 1 (founder round-3, 2026-06-11).
//
// Renders chapter/subsection-owned lesson images. Bare hashes normalize to
// /img_sign_by_hash/; bare numeric PNGs normalize to /img_sign/; path-qualified
// refs pass through.
// When there are no refs the component renders NOTHING — no placeholder, since
// a guided chapter without an associated sign is just prose (founder: omit when
// none). The alt is the canonical sign name when the backend provides it.
// ---------------------------------------------------------------------------

interface ChapterSignImage {
  ref: string;
  name: string | null;
}

/** Normalize a relative sign-asset ref to a public src (mirror of the read
 *  `toAssetSrc`). */
function signRefToSrc(ref: string): string {
  return lessonAssetSrc(ref);
}

function guidedImageRef(ref: string): string {
  return normalizeLessonAssetRef(ref);
}

function ChapterSignImages({
  refs,
  images,
  chapterTitle,
}: {
  refs: string[];
  images?: ChapterSignImage[];
  chapterTitle: string;
}): JSX.Element | null {
  const t = useTokens();
  const source = images && images.length > 0
    ? images
    : refs.map((ref) => ({ ref, name: null }));
  const clean = source.filter((img) => Boolean(img.ref));
  if (clean.length === 0) return null;
  const multiImage = clean.length > 1;

  return (
    <div
      data-testid="guided-chapter-signs"
      style={{
        margin: '6px 0 14px',
        display: 'grid',
        gridTemplateColumns: multiImage ? 'repeat(auto-fit, minmax(180px, 1fr))' : '1fr',
        gap: 10,
        justifyItems: 'center',
      }}
    >
      {clean.map((img, i) => (
        <span
          key={`${img.ref}-${i}`}
          data-sign-frame
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: t.surface,
            border: `1px solid ${t.line}`,
            borderRadius: t.radius.md,
            minHeight: multiImage ? 112 : 156,
            padding: 8,
            maxWidth: multiImage ? '100%' : 320,
            width: '100%',
          }}
        >
          <img
            src={signRefToSrc(img.ref)}
            alt={
              img.name ?? (clean.length > 1
                ? `Segnale ${i + 1} di ${clean.length} per ${chapterTitle}`
                : `Segnale per ${chapterTitle}`)
            }
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: multiImage ? 96 : 180,
              width: 'auto',
              height: 'auto',
              // QA-010 residual (fix campaign F5): before load a width-only flex
              // child computed height 0 → the strip had no box and content
              // shifted when images arrived. Reserving the min box keeps the
              // strip stable pre-load; after load height:auto + objectFit take
              // over and the rendered size is unchanged.
              minHeight: multiImage ? 64 : 96,
              minWidth: 64,
              objectFit: 'contain',
              borderRadius: t.radius.sm,
            }}
            decoding="async"
            loading="lazy"
            onError={(e) => {
              const frame = e.currentTarget.closest('[data-sign-frame]');
              if (frame instanceof HTMLElement) frame.style.display = 'none';
            }}
          />
          <SelectProfFigureButton selection={{
            reference: { kind: 'asset', imageRef: guidedImageRef(img.ref) },
            imageRef: guidedImageRef(img.ref), label: img.name ?? `Figura: ${chapterTitle}`,
          }} />
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BlockSegment — prose + optional scenario image.
// ---------------------------------------------------------------------------

function BlockSegment({
  segment,
  chapterTitle,
}: {
  segment: Extract<GuidedLessonSegment, { kind: 'block' }>;
  chapterTitle: string;
}): JSX.Element {
  const t = useTokens();
  const isHeadline = segment.role === 'headline';
  const imageAlt = `Illustrazione: ${chapterTitle}`;

  return (
    <div style={{ marginTop: isHeadline ? 0 : 14 }}>
      {segment.label && (
        <div
          style={{
            fontFamily: t.fonts.mono,
            fontSize: 10,
            letterSpacing: 0.6,
            color: t.textFaint,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          {segment.label}
        </div>
      )}

      {/* Scenario / diagram images — guided sidecars may carry bare sign refs. */}
      {segment.image_ref && (
        <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <span style={{ display: 'inline-block', maxWidth: 320, width: '100%' }}>
            <InlineImage
              kind="scenario"
              id={segment.role}
              imageRef={guidedImageRef(segment.image_ref)}
              altOverride={imageAlt}
              hideWhenBroken
            />
            <SelectProfFigureButton selection={{
              reference: { kind: 'asset', imageRef: guidedImageRef(segment.image_ref) },
              imageRef: guidedImageRef(segment.image_ref), label: imageAlt,
            }} />
          </span>
        </div>
      )}
      {(segment.alt_image_refs ?? []).map((ref, i) => (
        <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <span style={{ display: 'inline-block', maxWidth: 320, width: '100%' }}>
            <InlineImage
              kind="scenario"
              id={`${segment.role}-alt-${i}`}
              imageRef={guidedImageRef(ref)}
              altOverride={imageAlt}
              hideWhenBroken
            />
            <SelectProfFigureButton selection={{
              reference: { kind: 'asset', imageRef: guidedImageRef(ref) },
              imageRef: guidedImageRef(ref), label: imageAlt,
            }} />
          </span>
        </div>
      ))}

      <ProfProse text={segment.body_md} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuizCard — woven inline check. V/F uses the shared reply pills; the why/repair
// reveal on answer.
// ---------------------------------------------------------------------------

function QuizCard({
  segment,
  answer,
  pending = false,
  retryAnswer,
  onAnswer,
}: {
  segment: Extract<GuidedLessonSegment, { kind: 'quiz' }>;
  answer: QuizAnswer | undefined;
  pending?: boolean;
  retryAnswer?: QuizAnswer;
  onAnswer: (answer: QuizAnswer) => void;
}): JSX.Element {
  const t = useTokens();
  const quiz = segment.quiz;
  const answered = answer !== undefined;
  const locked = answered || pending;
  const wasCorrect = answer?.correct === true;
  const shownAtRef = useRef(Date.now());

  function handlePick(picked: number): void {
    if (locked) return;
    onAnswer({
      picked,
      correct: picked === quiz.correct_index,
      responseTimeMs: Date.now() - shownAtRef.current,
    });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <ProfMessage
        tone={answered ? (wasCorrect ? 'right' : 'wrong') : undefined}
        toneLabel={answered ? (wasCorrect ? 'Giusto' : 'Ripassiamo') : undefined}
        showHeader
      >
        <div data-testid="guided-quiz-prompt">
          <ProfProse text={quiz.prompt_md} />
        </div>

        {/* Answer controls */}
        {retryAnswer && !pending && !answered ? (
          <button type="button" onClick={() => onAnswer(retryAnswer)}
            style={{ marginTop: 12, padding: '10px 16px', borderRadius: t.radius.md,
              background: t.accent, color: t.surface, border: 0, fontFamily: t.fonts.body, fontWeight: 600 }}>
            Riprova salvataggio
          </button>
        ) : quiz.type === 'vf' ? (
          pending ? (
            <div data-testid="guided-quiz-saving" style={{ marginTop: 12, fontFamily: t.fonts.body, fontSize: 13, color: t.textMuted }}>
              Sto salvando la risposta...
            </div>
          ) : !answered ? (
            <VeroFalsoReplies
              onPick={(v: VFAnswer) => handlePick(v === 'Vero' ? vfIndexOf(quiz.options, 'Vero') : vfIndexOf(quiz.options, 'Falso'))}
            />
          ) : null
        ) : (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {quiz.options.map((opt, i) => {
              const picked = answer?.picked === i;
              const correct = quiz.correct_index === i;
              const showState = answered && (picked || correct);
              return (
                <button
                  key={i}
                  onClick={() => handlePick(i)}
                  disabled={locked}
                  data-testid={`guided-quiz-option-${i}`}
                  style={{
                    textAlign: 'left',
                    background: showState ? (correct ? t.rightSoft : t.wrongSoft) : t.surface,
                    border: `1px solid ${showState ? (correct ? t.right : t.wrong) : t.lineStrong}`,
                    borderRadius: t.radius.md,
                    padding: '10px 14px',
                    minHeight: 44,
                    cursor: locked ? 'default' : 'pointer',
                    color: t.text,
                    fontFamily: t.fonts.body,
                    fontSize: 14,
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {/* The "perché" + the repair line (on wrong) — revealed after answering. */}
        {answered && (
          <div data-testid="guided-quiz-feedback" style={{ marginTop: 14 }}>
            <div style={{ fontFamily: t.fonts.mono, fontSize: 10, letterSpacing: 0.6, color: t.textFaint, textTransform: 'uppercase', marginBottom: 6 }}>
              Perché
            </div>
            <ProfProse text={answer.feedback?.whyMd ?? 'Risposta già salvata. La spiegazione originale non è disponibile.'} size={15} />
            {!wasCorrect && answer.feedback?.repairMd && (
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: t.surface2,
                  border: `1px solid ${t.line}`,
                  borderRadius: t.radius.md,
                }}
              >
                <ProfProse text={answer.feedback.repairMd} size={14} />
              </div>
            )}
          </div>
        )}
      </ProfMessage>
    </div>
  );
}

function GuidedCelebration({
  copy,
  stats,
  chapterCount,
  onReturn,
}: {
  copy: ReturnType<typeof guidedFinishCopy> | null;
  stats: GuidedLessonProgressSummary | null;
  chapterCount: number;
  onReturn: () => void | Promise<void>;
}): JSX.Element {
  const t = useTokens();
  const reducedMotion = useReducedMotion();
  const resolvedCopy = copy ?? {
    title: 'Lezione letta',
    body: 'Hai letto la lezione.',
    detail: 'Lettura completata',
    tone: 'neutral' as const,
  };
  const accent = resolvedCopy.tone === 'success'
    ? t.right
    : resolvedCopy.tone === 'review'
      ? t.wrong
      : t.accent;
  // V29 S3 (founder R1-3): a real completion moment — sparkles (killed by
  // reduced-motion) + honest stats tiles. Every figure is REAL summary data
  // (F19: no fabricated deltas — a preparation change only appears when the
  // contract-2 read model provides evidence, which this screen does not).
  const sparkColors = [t.accent, '#e547c4', '#ffd76e', t.right];
  return (
    <div
      data-testid="guided-celebration"
      style={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '70%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 14,
        padding: '36px 12px',
        color: t.text,
        fontFamily: t.fonts.body,
      }}
    >
      {!reducedMotion && resolvedCopy.tone !== 'review' && sparkColors.map((c, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: `${14 + i * 22}%`,
            width: 8,
            height: 8,
            borderRadius: 2,
            background: c,
            opacity: 0.85,
            animation: `asc-celebrate-fall 2.8s cubic-bezier(0.22, 0.61, 0.36, 1) ${i * 0.45}s infinite`,
          }}
        />
      ))}
      <div
        aria-hidden="true"
        style={{
          width: 84,
          height: 84,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: resolvedCopy.tone === 'success' ? t.brandGradient : t.accentSoft,
          border: resolvedCopy.tone === 'success' ? 'none' : `2px solid ${accent}`,
          boxShadow: resolvedCopy.tone === 'success' ? '0 8px 22px rgba(105, 67, 255, 0.28)' : 'none',
        }}
      >
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12.5 9.2 16 19 7"
            stroke={resolvedCopy.tone === 'success' ? '#fff' : accent}
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div
        role="heading"
        aria-level={1}
        style={{
          fontFamily: t.fonts.heading,
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: 0,
        }}
      >
        {resolvedCopy.title}
      </div>
      <p style={{ margin: 0, maxWidth: 320, fontSize: 16, lineHeight: 1.5, color: t.textMuted }}>
        {resolvedCopy.body}
      </p>
      {/* Honest lesson stats — chapters read + quiz outcome (only when the
          lesson HAS integrated questions and at least one was answered). */}
      <div
        data-testid="guided-celebration-stats"
        style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 340, justifyContent: 'center' }}
      >
        <div style={{ flex: 1, background: t.surface2 ?? t.accentSoft, borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{chapterCount}</div>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>capitoli letti</div>
        </div>
        {stats && stats.answeredCount > 0 && stats.requiredQuizCount > 0 && (
          <div style={{ flex: 1, background: t.surface2 ?? t.accentSoft, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {stats.correctCount}/{stats.requiredQuizCount}
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>quiz giusti</div>
          </div>
        )}
      </div>
      <button
        data-testid="guided-celebration-cta"
        onClick={() => void onReturn()}
        style={{
          marginTop: 8,
          minHeight: 44,
          border: 'none',
          borderRadius: t.radius.pill,
          padding: '12px 22px',
          background: t.accent,
          color: '#fff',
          cursor: 'pointer',
          fontFamily: t.fonts.body,
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: 0,
        }}
      >
        Torna alle lezioni
      </button>
    </div>
  );
}

// Resolve the index of a Vero/Falso label in the quiz's options array. The
// baked options are ['Vero','Falso'] but we match defensively (case-insensitive)
// rather than assuming index 0 = Vero, so a flipped authoring order is honored.
function vfIndexOf(options: string[], label: 'Vero' | 'Falso'): number {
  const i = options.findIndex((o) => o.trim().toLowerCase() === label.toLowerCase());
  return i >= 0 ? i : (label === 'Vero' ? 0 : 1);
}


// ---------------------------------------------------------------------------
// Shared button styles (design tokens only — dark mode for free).
// ---------------------------------------------------------------------------

function secondaryBtn(t: ReturnType<typeof useTokens>): React.CSSProperties {
  return {
    background: t.surface,
    color: t.text,
    border: `1px solid ${t.lineStrong}`,
    borderRadius: t.radius.pill,
    padding: '11px 20px',
    minHeight: 44,
    cursor: 'pointer',
    fontFamily: t.fonts.body,
    fontSize: 14,
    fontWeight: 600,
  };
}
